/**
 * Field Mapping catalog helpers.
 *
 * The Field Mapping UI must show every editable vehicle field independently of feed
 * availability: a feed only determines which source OPTIONS a field has, never whether
 * the field appears. These pure helpers reconcile the editable field catalog (returned
 * by `mapping-config/fields`) with the rows transformed from the saved/effective config
 * so that catalog fields with no saved mapping still render as empty, configurable rows.
 */

/** A single editable field from the `mapping-config/fields` catalog. */
export interface FieldMappingCatalogField {
  key: string
  /** Source options available for this field (may be empty when no feed provides it). */
  sources?: string[]
  /** Backend-selected initial source for an unmapped field. */
  recommendedSource?: string
  /** Suggested starter default (e.g. state -> "Ready for Sale"); surfaced as an input hint only. */
  starterDefaultValue?: unknown
}

/** A transformed UI field row (subset of the shape produced by transformConfigForUI). */
export interface UiFieldRow {
  name: string
  custom?: boolean
  sources?: unknown[]
  values?: { source?: string; fallback?: Array<{ source?: string }> }
  policy?: { on_missing?: string; default_value?: unknown }
  /** Display-only hint copied from the catalog; never persisted. */
  starterDefaultValue?: unknown
  [key: string]: unknown
}

export const DERIVED_FEATURE_FIELD_NAMES: ReadonlySet<string> = new Set([
  'features_json',
  'features_structured',
  'features_sidebar',
])

export function isDerivedFeatureFieldName(fieldName: unknown): boolean {
  return DERIVED_FEATURE_FIELD_NAMES.has(String(fieldName ?? '').trim())
}

export function collectLegacyDerivedFeatureSelections(
  config: Record<string, any> | null | undefined,
  segmentId = 'all',
): {
  selections: Record<string, string[]>
  defaultValues: Record<string, unknown>
} {
  const selections: Record<string, string[]> = {}
  const defaultValues: Record<string, unknown> = {}
  const mappings = config?.mapping_config?.[segmentId]?.field_mapping?.mappings
  if (!Array.isArray(mappings)) return { selections, defaultValues }

  for (const mapping of mappings) {
    const field = String(mapping?.field ?? '').trim()
    if (!isDerivedFeatureFieldName(field)) continue

    selections[field] = Array.isArray(mapping?.sources)
      ? [...mapping.sources]
      : typeof mapping?.sources === 'string' && mapping.sources
        ? [mapping.sources]
        : []

    const defaultValue = mapping?.default_value ?? mapping?.policy?.default_value
    if (defaultValue !== undefined) {
      defaultValues[field] = defaultValue
    }
  }

  return { selections, defaultValues }
}

/**
 * A row is "unconfigured" when the dealer has not given it any meaning: no selected
 * source, no fallback source, and no default value. Such rows are shown so a dealer can
 * configure them, but must never be treated as a change or persisted on save.
 *
 * Custom fields are always considered configured (they are explicitly authored).
 */
export function isUnconfiguredFieldRow(row: UiFieldRow | null | undefined): boolean {
  if (!row) return true
  if (row.custom) return false

  const source = String(row.values?.source ?? '').trim()
  const hasFallback = Array.isArray(row.values?.fallback)
    && row.values.fallback.some((entry) => String(entry?.source ?? '').trim().length > 0)
  const rawDefault = row.policy?.default_value
  const hasDefault =
    rawDefault !== undefined && rawDefault !== null && String(rawDefault).trim().length > 0

  return source.length === 0 && !hasFallback && !hasDefault
}

/**
 * Build an unconfigured row for a catalog field that has no saved mapping.
 * Available sources are exposed as choices, but a backend recommendation is not
 * materialized here: doing so would make a newly introduced field look persisted
 * and leave the UI with no user action to stage it. First-run defaults are handled
 * separately by buildRecommendedCatalogMappings.
 */
export function buildEmptyCatalogRow(field: FieldMappingCatalogField): UiFieldRow {
  const availableSources = Array.isArray(field.sources) ? [...field.sources] : []
  return {
    name: field.key,
    sources: availableSources,
    values: { source: '', fallback: [] },
    required: false,
    policy: { on_missing: 'fallback' },
    ...(field.starterDefaultValue !== undefined
      ? { starterDefaultValue: field.starterDefaultValue }
      : {}),
  }
}

export function buildRecommendedCatalogMappings(
  fields: FieldMappingCatalogField[],
): Array<Record<string, unknown>> {
  return fields.map((field) => {
    const availableSources = Array.isArray(field.sources) ? field.sources : []
    const recommendedSource =
      field.recommendedSource && availableSources.includes(field.recommendedSource)
        ? field.recommendedSource
        : undefined

    return {
      field: field.key,
      sources: recommendedSource ? [recommendedSource] : [],
      required: false,
      policy: { on_missing: 'fallback' },
      ...(field.starterDefaultValue !== undefined
        ? { default_value: field.starterDefaultValue }
        : {}),
    }
  })
}

export function buildRecommendedCatalogSelections(fields: FieldMappingCatalogField[]): {
  selections: Record<string, string | string[]>
  defaultValues: Record<string, unknown>
} {
  const selections: Record<string, string | string[]> = {}
  const defaultValues: Record<string, unknown> = {}

  for (const field of fields) {
    const availableSources = Array.isArray(field.sources) ? field.sources : []
    const recommendedSource =
      field.recommendedSource && availableSources.includes(field.recommendedSource)
        ? field.recommendedSource
        : undefined

    if (recommendedSource) {
      selections[field.key] = recommendedSource
    } else if (field.starterDefaultValue !== undefined) {
      selections[field.key] = []
    }

    if (field.starterDefaultValue !== undefined) {
      defaultValues[field.key] = field.starterDefaultValue
    }
  }

  return { selections, defaultValues }
}

/**
 * Ensure every catalog field is represented as a row in the given segment of a UI config,
 * mutating it in place. Existing rows (including custom fields and feed-backed mappings)
 * are preserved untouched; only missing catalog fields are appended as empty rows.
 *
 * @returns the same `uiConfig` reference for chaining.
 */
export function ensureCatalogFieldsPresent(
  uiConfig: Record<string, UiFieldRow[]>,
  catalogFields: FieldMappingCatalogField[],
  segmentId: string,
): Record<string, UiFieldRow[]> {
  const segmentRows = Array.isArray(uiConfig[segmentId]) ? uiConfig[segmentId] : []
  const existingNames = new Set(segmentRows.map((row) => row?.name))

  for (const field of catalogFields) {
    if (
      !field?.key ||
      isDerivedFeatureFieldName(field.key) ||
      existingNames.has(field.key)
    ) {
      continue
    }
    segmentRows.push(buildEmptyCatalogRow(field))
    existingNames.add(field.key)
  }

  uiConfig[segmentId] = segmentRows
  return uiConfig
}
